import { ComponentProps, useMemo } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import { TasksValueObjectsTaskCategoryEnum } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

type GetModuleResult = Omit<NonNullable<ComponentProps<typeof F0Dialog>['module']>, 'href'>

const taskCategoryToModule: Record<
  TasksValueObjectsTaskCategoryEnum,
  { id: GetModuleResult['id']; label: () => string }
> = {
  benefits: {
    id: 'benefits',
    label: () => i18n.t('benefits.module_name'),
  },
  documents: {
    id: 'documents',
    label: () => i18n.t('sidebar.documents'),
  },
  engagement: {
    id: 'engagement',
    label: () => i18n.t('sidebar.engagement'),
  },
  finance: {
    id: 'finance-treasury',
    label: () => i18n.t('sidebar.finance'),
  },
  organization: {
    id: 'employees',
    label: () => i18n.t('sidebar.team'),
  },
  performance: {
    id: 'performance_v2',
    label: () => i18n.t('sidebar.performance_new'),
  },
  recruitment: {
    id: 'ats',
    label: () => i18n.t('sidebar.recruitment'),
  },
  software: {
    id: 'software',
    label: () => i18n.t('finance.modules.spending.software'),
  },
  spending: {
    id: 'my_spending',
    label: () => i18n.t('sidebar.purchases'),
  },
  surveys: {
    id: 'engagement',
    label: () => i18n.t('sidebar.engagement'),
  },
  timeoff: {
    id: 'timeoff',
    label: () => i18n.t('sidebar.time_off'),
  },
  time_planning: {
    id: 'shifts',
    label: () => i18n.t('sidebar.shift_management'),
  },
  time_tracking: {
    id: 'time-tracking',
    label: () => i18n.t('sidebar.time_tracking'),
  },
  training: {
    id: 'my_trainings',
    label: () => i18n.t('sidebar.trainings'),
  },
  complaints: {
    id: 'engagement',
    label: () => i18n.t('sidebar.complaints'),
  },
  compensation: {
    id: 'benefits',
    label: () => i18n.t('sidebar.compensation'),
  },
  policies: {
    id: 'pages',
    label: () => i18n.t('sidebar.policies'),
  },
}

export const getModule: (
  category?: TasksValueObjectsTaskCategoryEnum
) => GetModuleResult | undefined = (category) => {
  if (!category)
    return {
      id: 'tasks',
      label: i18n.t('tasks.header'),
    }

  const mapping = category in taskCategoryToModule ? taskCategoryToModule[category] : undefined

  if (!mapping) return

  return {
    id: mapping.id,
    label: mapping.label(),
  }
}

export const useModuleHref = (moduleId?: TasksValueObjectsTaskCategoryEnum) => {
  const { navigation } = useNavigation()

  const hrefs = useMemo<Record<TasksValueObjectsTaskCategoryEnum, string>>(
    () => ({
      timeoff: navigation.timeOff.fullPath(),
      time_planning: navigation.shifts.fullPath(),
      time_tracking: navigation.timeTracking.fullPath(),
      training: navigation.trainings.fullPath(),
      benefits: navigation.benefits.fullPath(),
      compensation: navigation.compensations.fullPath(),
      documents: navigation.documents.fullPath(),
      engagement: navigation.engagement.fullPath(),
      finance: navigation.finance.fullPath(),
      organization: navigation.employees.fullPath(),
      performance: navigation.performanceProcess.fullPath(),
      recruitment: navigation.ats.fullPath(),
      software: navigation.software.fullPath(),
      spending: navigation.expenses.fullPath(),
      surveys: navigation.engagement.surveys.fullPath(),
      complaints: navigation.complaints.fullPath(),
      policies: navigation.pages.fullPath(),
    }),
    [navigation]
  )

  const moduleHref = moduleId ? hrefs[moduleId] : navigation.tasks.fullPath()

  return moduleHref
}
